import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Sun, Moon, Shield, Users, Zap, Building2, ArrowRight, Loader2 } from 'lucide-react';
import '@/styles/login.css';

// Import Firebase configuration and authentication
import { auth, firebaseAuth, firebaseGetMultiFactorResolver } from '../../../js/external-js/config/main_config';
import firebase from 'firebase/compat/app';
import { useNavigate } from 'react-router-dom';
import { getAvailableFeatures } from '@/utils/featuresUtil';
import { handlePostLogin, discoverDomain, getFirebaseSSOProvider } from '@/services/authDomainService';

// Theme types
type LoginTheme = 'light' | 'dark' | 'system';

// Get initial theme from localStorage or default to system
const getInitialTheme = (): LoginTheme => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('login-theme');
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  }
  return 'system';
};

// Apply theme to document
const applyTheme = (theme: LoginTheme) => {
  if (typeof document === 'undefined') return;
  
  if (theme === 'system') {
    document.documentElement.removeAttribute('data-login-theme');
  } else {
    document.documentElement.setAttribute('data-login-theme', theme);
  }
};

// Feature cards data
const FEATURES = [
  {
    icon: Shield,
    title: 'Security First',
    description: 'Enterprise-grade security for your compliance needs',
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Work together seamlessly across your organization',
  },
  {
    icon: Zap,
    title: 'Automated Workflows',
    description: 'Streamline your GRC processes with automation',
  },
];

const Login = () => {
  const features = getAvailableFeatures();
  const isSelfServiceEnabled = features.find((feature) => feature.name === 'Self-Service');
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSignUp, setIsLoadingSignUp] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  // Theme state
  const [theme, setTheme] = useState<LoginTheme>(getInitialTheme);
  const [isDark, setIsDark] = useState(false);

  // Theme effect
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('login-theme', theme);
    
    // Determine if we're in dark mode
    if (theme === 'system') {
      setIsDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
    } else {
      setIsDark(theme === 'dark');
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        setIsDark(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Toggle theme
  const toggleTheme = () => {
    if (theme === 'system') {
      setTheme(isDark ? 'light' : 'dark');
    } else if (theme === 'dark') {
      setTheme('light');
    } else {
      setTheme('dark');
    }
  };

  // State for additional UI components
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [resolver, setResolver] = useState<any>(null);

  // State for IDP/SSO login
  const [showIDPDialog, setShowIDPDialog] = useState(false);
  const [idpEmail, setIdpEmail] = useState('');
  const [idpStep, setIdpStep] = useState<'email' | 'discovered' | 'error'>('email');
  const [idpDiscoveryResult, setIdpDiscoveryResult] = useState<any>(null);
  const [idpLoading, setIdpLoading] = useState(false);
  const [idpError, setIdpError] = useState('');

  // Handle email/password authentication
  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setShowError(false);

    if (email.length > 1 && password.length >= 8) {
      setIsLoading(true);

      try {
        const result = await auth.signInWithEmailAndPassword(email, password);
        
        // Call postLogin to handle authorised domain auto-join and user creation
        if (result.user) {
          const idToken = await result.user.getIdToken();
          const postLoginResult = await handlePostLogin(idToken);
          console.log('[Login] postLogin result:', postLoginResult);
          
          // Check if user is pending approval
          if (postLoginResult.success && postLoginResult.pendingApproval) {
            // Store pending info for the pending approval page
            sessionStorage.setItem('pendingApprovalInfo', JSON.stringify({
              tenancyId: postLoginResult.tenancyId,
              tenancyName: postLoginResult.tenancyName,
            }));
            navigate('/pending-approval');
            return;
          }
        }
        // If successful, auth state change will redirect
      } catch (error: any) {
        console.log('Error:', error);

        if (error.code === 'auth/multi-factor-auth-required') {
          const mfaResolver = firebaseGetMultiFactorResolver(auth, error);
          setResolver(mfaResolver);
          setShowMFA(true);
        } else {
          setErrorMessage(error.message || 'Invalid email or password');
          setShowError(true);
          setIsLoading(false);
        }
      }
    } else {
      setErrorMessage('Please enter a valid email and password (min 8 characters)');
      setShowError(true);
      setIsLoading(false);
    }
  }

  // Handle Google sign in
  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setShowError(false);

    try {
      const provider = new firebaseAuth.GoogleAuthProvider();
      const result = await auth.signInWithPopup(provider);
      
      // Call postLogin to handle authorised domain auto-join and user creation
      if (result.user) {
        const idToken = await result.user.getIdToken();
        const postLoginResult = await handlePostLogin(idToken);
        console.log('[Login] postLogin result:', postLoginResult);
        
        // Check if user is pending approval
        if (postLoginResult.success && postLoginResult.pendingApproval) {
          sessionStorage.setItem('pendingApprovalInfo', JSON.stringify({
            tenancyId: postLoginResult.tenancyId,
            tenancyName: postLoginResult.tenancyName,
          }));
          navigate('/pending-approval');
          return;
        }
      }
      // If successful, auth state change will redirect
    } catch (error: any) {
      console.log(error);

      if (error.code === 'auth/multi-factor-auth-required') {
        const mfaResolver = firebaseGetMultiFactorResolver(auth, error);
        setResolver(mfaResolver);
        setShowMFA(true);
      } else {
        setErrorMessage(error.message || 'Google sign in failed');
        setShowError(true);
        setIsLoading(false);
      }
    }
  };

  // Handle Microsoft sign in
  const handleMicrosoftSignIn = async () => {
    setIsLoading(true);
    setShowError(false);

    try {
      const provider = new firebaseAuth.OAuthProvider('microsoft.com');
      const result = await auth.signInWithPopup(provider);
      
      // Call postLogin to handle authorised domain auto-join and user creation
      if (result.user) {
        const idToken = await result.user.getIdToken();
        const postLoginResult = await handlePostLogin(idToken);
        console.log('[Login] postLogin result:', postLoginResult);
        
        // Check if user is pending approval
        if (postLoginResult.success && postLoginResult.pendingApproval) {
          sessionStorage.setItem('pendingApprovalInfo', JSON.stringify({
            tenancyId: postLoginResult.tenancyId,
            tenancyName: postLoginResult.tenancyName,
          }));
          navigate('/pending-approval');
          return;
        }
      }
      // If successful, auth state change will redirect
    } catch (error: any) {
      console.log(error);

      if (error.code === 'auth/multi-factor-auth-required') {
        const mfaResolver = firebaseGetMultiFactorResolver(auth, error);
        setResolver(mfaResolver);
        setShowMFA(true);
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        // Handle account linking case
        setErrorMessage('This email is already associated with another sign-in method');
        setShowError(true);
      } else {
        setErrorMessage(error.message || 'Microsoft sign in failed');
        setShowError(true);
      }
      setIsLoading(false);
    }
  };

  // Handle IDP discovery - find SSO provider for email domain
  const handleIDPDiscover = async () => {
    if (!idpEmail || !idpEmail.includes('@')) {
      setIdpError('Please enter a valid email address');
      setIdpStep('error');
      return;
    }

    setIdpLoading(true);
    setIdpError('');

    try {
      const result = await discoverDomain(idpEmail);
      console.log('[Login] IDP discovery result:', result);

      if (result.found && result.ssoProvider) {
        setIdpDiscoveryResult(result);
        setIdpStep('discovered');
      } else if (result.found) {
        setIdpError('Your organization does not have SSO configured. Please use email/password or social login.');
        setIdpStep('error');
      } else {
        setIdpError('No organization found for this email domain. Please use email/password or social login.');
        setIdpStep('error');
      }
    } catch (error: any) {
      console.error('[Login] IDP discovery error:', error);
      setIdpError(error.message || 'Failed to discover organization');
      setIdpStep('error');
    } finally {
      setIdpLoading(false);
    }
  };

  // Handle SSO sign-in after discovery
  const handleSSOSignIn = async () => {
    if (!idpDiscoveryResult?.ssoProvider) {
      setIdpError('No SSO provider configured');
      return;
    }

    setIdpLoading(true);
    setShowError(false);

    try {
      const { ssoProvider } = idpDiscoveryResult;
      const provider = await getFirebaseSSOProvider(ssoProvider.type, ssoProvider.firebaseProviderId);
      
      if (!provider) {
        throw new Error('Failed to initialize SSO provider');
      }

      // Close dialog before popup
      setShowIDPDialog(false);

      const result = await auth.signInWithPopup(provider);
      
      // Call postLogin to handle authorised domain auto-join
      if (result.user) {
        const idToken = await result.user.getIdToken();
        const postLoginResult = await handlePostLogin(idToken);
        console.log('[Login] SSO postLogin result:', postLoginResult);
        
        if (postLoginResult.success && postLoginResult.pendingApproval) {
          sessionStorage.setItem('pendingApprovalInfo', JSON.stringify({
            tenancyId: postLoginResult.tenancyId,
            tenancyName: postLoginResult.tenancyName,
          }));
          navigate('/pending-approval');
          return;
        }
      }
      // Auth state change will handle redirect
    } catch (error: any) {
      console.error('[Login] SSO sign-in error:', error);
      
      if (error.code === 'auth/multi-factor-auth-required') {
        const mfaResolver = firebaseGetMultiFactorResolver(auth, error);
        setResolver(mfaResolver);
        setShowMFA(true);
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        setErrorMessage('This email is already associated with another sign-in method');
        setShowError(true);
      } else {
        setErrorMessage(error.message || 'SSO sign in failed');
        setShowError(true);
      }
      setIdpLoading(false);
    }
  };

  // Reset IDP dialog state
  const resetIDPDialog = () => {
    setIdpEmail('');
    setIdpStep('email');
    setIdpDiscoveryResult(null);
    setIdpError('');
    setIdpLoading(false);
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setErrorMessage('Please enter your email address');
      setShowError(true);
      return;
    }

    setIsLoading(true);
    setShowError(false);

    try {
      await auth.sendPasswordResetEmail(resetEmail);
      setResetEmailSent(true);
      setTimeout(() => {
        setShowPasswordReset(false);
        setResetEmailSent(false);
        setResetEmail('');
      }, 5000);
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to send reset email');
      setShowError(true);
    }
    setIsLoading(false);
  };

  // State for MFA verification
  const [verificationId, setVerificationId] = useState<string>('');
  const [phoneInfoOptions, setPhoneInfoOptions] = useState<any>(null);
  const [smsAppVerifier, setSmsAppVerifier] = useState<any>(null);

  // Auto-submit OTP when 6 digits are entered
  useEffect(() => {
    if (showMFA && verificationCode.length === 6 && !isLoading) {
      handleVerifyCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode, showMFA, isLoading]);

  // Generate reCAPTCHA verifier for MFA
  const generateRecaptcha = async () => {
    try {
      if (!smsAppVerifier) {
        const recaptchaVerifier = new firebaseAuth.RecaptchaVerifier(
          'recaptcha-container',
          {
            size: 'invisible',
            callback: () => {
              console.log('reCAPTCHA solved!');
            },
            'error-callback': (error: any) => {
              console.error('reCAPTCHA error:', error);
              setErrorMessage('Failed to verify reCAPTCHA. Please try again.');
              setShowError(true);
            }
          },
          auth.app
        );

        await recaptchaVerifier.render();
        setSmsAppVerifier(recaptchaVerifier);
        return recaptchaVerifier;
      }
      return smsAppVerifier;
    } catch (error: any) {
      console.error('Error generating reCAPTCHA:', error);
      setErrorMessage('Failed to initialize verification. Please try again.');
      setShowError(true);
      setIsLoading(false);
      return null;
    }
  };

  // Send verification code when MFA is shown
  useEffect(() => {
    if (showMFA && resolver) {
      const sendVerificationCode = async () => {
        try {
          const appVerifier = await generateRecaptcha();
          if (!appVerifier) return;

          // Get the first phone hint (we could add UI to select from multiple hints if needed)
          const hint = resolver.hints[0];

          // Setup phone info options for verification
          const options = {
            multiFactorHint: hint,
            session: resolver.session
          };
          setPhoneInfoOptions(options);

          // Create phone auth provider and verify phone number
          const phoneAuthProvider = new firebaseAuth.PhoneAuthProvider(auth);
          const verificationId = await phoneAuthProvider.verifyPhoneNumber(options, appVerifier);
          setVerificationId(verificationId);

          // Display last 4 digits of the phone number
          const phoneNumber = hint.phoneNumber;
          const lastFourDigits = phoneNumber.slice(-4);
          console.log(`Verification code sent to number ending in ${lastFourDigits}`);

          // Reset loading state after verification code is sent
          setIsLoading(false);
        } catch (error: any) {
          console.error('Error sending verification code:', error);
          setErrorMessage('Failed to send verification code. Please try again.');
          setShowError(true);
          setIsLoading(false);
        }
      };

      sendVerificationCode();
    }
  }, [showMFA, resolver]);

  // Handle MFA verification
  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setErrorMessage('Please enter a valid 6-digit verification code');
      setShowError(true);
      return;
    }

    setIsLoading(true);
    setShowError(false);

    try {
      if (!verificationId || !resolver) {
        throw new Error('Verification session expired. Please try signing in again.');
      }

      // Create credential with verification ID and code
      const credential = firebaseAuth.PhoneAuthProvider.credential(verificationId, verificationCode);

      // Create multi-factor assertion
      const multiFactorAssertion = firebaseAuth.PhoneMultiFactorGenerator.assertion(credential);

      console.log('Multi-factor assertion:', multiFactorAssertion);

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('MFA sign-in timed out')), 30000);
      });

      try {
        // Resolve sign in with the assertion with timeout
        await Promise.race([resolver.resolveSignIn(multiFactorAssertion), timeoutPromise]);
        console.log('MFA sign-in successful!');

        // Force redirect if auth state change doesn't trigger
        setTimeout(() => {
          const user = auth.currentUser;
          if (user) {
            // Handle redirect manually if needed
            const urlParams = new URLSearchParams(window.location.search);
            let returnUrl = urlParams.get('returnUrl');

            if (returnUrl === '%2F') {
              returnUrl = null;
            }

            if (returnUrl) {
              window.location.href = returnUrl.replace('%2F', '/');
            } else {
              window.location.href = '/';
            }
          }
        }, 2000);
      } catch (timeoutError) {
        console.error('MFA resolution timed out:', timeoutError);
        throw new Error('MFA verification timed out. Please try again.');
      }

      // Auth state change listener will handle redirect after successful sign-in
    } catch (error: any) {
      console.error('Error verifying code:', error);
      setErrorMessage(error.message || 'Invalid verification code');
      setShowError(true);
      setIsLoading(false);

      // Clear the verification code on error
      setVerificationCode('');

      // Reset MFA flow on error
      if (error.message?.includes('timed out') || error.code === 'auth/timeout') {
        // Reset the MFA flow completely
        setShowMFA(false);
        if (smsAppVerifier) {
          smsAppVerifier.clear();
          setSmsAppVerifier(null);
        }
        setResolver(null);
        setVerificationId('');
      }
      // If reCAPTCHA verification failed, try to regenerate it
      else if (error.code === 'auth/invalid-recaptcha-token') {
        if (smsAppVerifier) {
          smsAppVerifier.clear();
          setSmsAppVerifier(null);
        }
        await generateRecaptcha();
      }
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const token = await user.getIdToken();
        const uid = user.uid;
        // Set cookie with SameSite=None and Secure, and DO NOT set Domain so each subdomain gets a unique cookie
        document.cookie = `__session_react=${token}--__--${uid}; Path=/; SameSite=None; Secure`;


        // Check user role in Firestore before redirecting
        const db = firebase.firestore();
        try {
          const userDoc = await db.collection('users').doc(uid).get();

          if (userDoc.exists && userDoc.data().roles) {
            const userData = userDoc.data();

            // Handle role-based redirection
            if (userData.roles.user === true) {
              // Check if we're in a development/special environment
              const hostname = window.location.hostname;
              const isDevelopmentEnv =
                hostname.includes('localhost') ||
                hostname.includes('127.0.0.1') ||
                hostname.endsWith('.local') ||
                hostname.includes('deiterate-hybrid.web.app');

              // Use app.deit.dev for development environments, otherwise use production URL format
              if (isDevelopmentEnv) {
                window.location.href = 'https://app.deit.dev';
              } else {
                // For production: construct app subdomain URL from the current hostname
                const domainParts = hostname.split('.');
                if (domainParts.length >= 2) {
                  // Ensures we have at least domain.tld
                  window.location.href = `https://app.${domainParts[domainParts.length - 2]}.${domainParts[domainParts.length - 1]}`;
                } else {
                  // Fallback in case hostname parsing fails
                  window.location.href = 'https://app.deit.dev';
                }
              }
              return;
            }
          }

          // If no special role or document doesn't exist, use default redirect logic
          const urlParams = new URLSearchParams(window.location.search);
          let returnUrl = urlParams.get('returnUrl');

          if (returnUrl === '%2Findex_vanilla.html' || returnUrl === '%2Fdashboard') {
            returnUrl = null;
          }

          if (returnUrl) {
            window.location.href = returnUrl.replace('%2F', '/');
          } else {
            // window.location.href = 'index_vanilla.html';
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Error checking user role:', error);
          // Fall back to default redirect if there's an error
          // window.location.href = 'index_vanilla.html';
          navigate('/dashboard');
        }
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="auth-container">
      {/* Hidden reCAPTCHA container for MFA */}
      <div id="recaptcha-container" style={{ display: 'none' }}></div>
      {/* Desktop view - two column layout */}
      <div className="desktop-view">
        {/* Animated gradient background - spans both columns */}
        <div className="gradient-overlay"></div>
        <div className="glow-1"></div>
        <div className="glow-2"></div>
        
        {/* Left column - Branding panel */}
        <div className="left-column">
          {/* Content - centered vertically like Controlyze */}
          <div className="left-content">
              {/* Duck mascot and branding */}
              <div className="logo-container">
                <div className="logo-glow"></div>
                <img 
                  src="/assets/ducks/sunglasses_left.svg" 
                  alt="de.iterate Duck" 
                  className="brand-logo" 
                />
                <h1 className="brand-title">de.iterate</h1>
                <p className="brand-tagline">Your GRC platform, simplified.</p>
              </div>
              
              {/* Feature cards */}
              <div className="feature-cards">
                {FEATURES.map((feature, i) => (
                  <div key={i} className="feature-card">
                    <div className="feature-icon">
                      <feature.icon />
                    </div>
                    <div className="feature-content">
                      <h3>{feature.title}</h3>
                      <p>{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
          </div>
        </div>

        {/* Right column - login form */}
        <div className="right-column">
          {/* Theme toggle */}
          <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {isDark ? <Sun /> : <Moon />}
          </button>
          
          <div className="auth-form-container">
            {!showMFA ? (
              <div className="auth-header">
                <h1>Welcome back</h1>
                <p>Sign in with your credentials to continue</p>
              </div>
            ) : (
              <div className="auth-header">
                <h1>Two-factor Authentication</h1>
                <p>Enter the verification code sent to your phone</p>
              </div>
            )}

            {/* Auth Form */}
            <div className="auth-form">
              {showPasswordReset ? (
                <div className="password-reset-form">
                  <h2>Reset your password</h2>
                  <p>Enter your email address and we'll send you a link to reset your password.</p>
                  {showError && <div className="error-message">{errorMessage}</div>}
                  <div className="form-group">
                    <Input
                      id="reset-email"
                      placeholder="name@example.com"
                      type="email"
                      autoCapitalize="none"
                      autoComplete="email"
                      autoCorrect="off"
                      disabled={isLoading || resetEmailSent}
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="mb-4"
                    />
                  </div>
                  <div className="form-actions">
                    <Button className="reset-button" disabled={isLoading || resetEmailSent} onClick={handlePasswordReset}>
                      {isLoading && (
                        <svg
                          className="spinner-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                      )}
                      {resetEmailSent ? 'Email Sent' : 'Send Reset Link'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowPasswordReset(false);
                        setShowError(false);
                      }}
                      disabled={isLoading}>
                      Back to Login
                    </Button>
                  </div>
                </div>
              ) : showMFA ? (
                <div className="mfa-form">
                  {showError && <div className="error-message">{errorMessage}</div>}
                  <div className="form-group">
                    <InputOTP maxLength={6} value={verificationCode} onChange={setVerificationCode} disabled={isLoading} className="mb-4">
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <Button className="verify-button" disabled={isLoading} onClick={handleVerifyCode}>
                    {isLoading && (
                      <svg
                        className="spinner-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                    )}
                    Verify
                  </Button>
                </div>
              ) : (
                <>
                  <form onSubmit={(e) => {
                    if (!isLoading) {
                      onSubmit(e);
                    } else {
                      e.preventDefault();
                    }
                  }}>
                    {showError && <div className="error-message">{errorMessage}</div>}
                    <div className="form-group">
                      <label htmlFor="email">Email</label>
                      <Input
                        id="email"
                        placeholder="Enter your email"
                        type="email"
                        autoCapitalize="none"
                        autoComplete="email"
                        autoCorrect="off"
                        disabled={isLoading}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="password">Password</label>
                      <Input
                        id="password"
                        placeholder="Enter your password"
                        type="password"
                        autoCapitalize="none"
                        autoComplete="current-password"
                        autoCorrect="off"
                        disabled={isLoading}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <div className="forgot-password">
                        <button
                          type="button"
                          className="forgot-password-link"
                          onClick={() => {
                            setShowPasswordReset(true);
                            setResetEmail(email);
                            setShowError(false);
                          }}>
                          Forgot password?
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="sign-in-button" disabled={isLoading}>
                      {isLoading && (
                        <svg
                          className="spinner-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                      )}
                      Sign In with Email
                    </Button>
                    {isSelfServiceEnabled ? (
                      <Button type="button" variant="outline" className="w-full mt-4" disabled={isLoadingSignUp || isLoading} onClick={() => {
                        navigate('/signup');
                      }}>
                        Sign Up
                      </Button>
                    ) : null}
                  </form>
                  <div className="divider">
                    <span>Or continue with</span>
                  </div>
                  <div className="social-buttons">
                    <Button 
                      variant={isDark ? "secondary" : "outline"} 
                      disabled={isLoading} 
                      className="social-button google-button" 
                      onClick={handleGoogleSignIn}
                    >
                      <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3276 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z"
                          fill="#34A853"
                        />
                        <path d="M5.50253 14.3003C4.99987 12.8099 4.99987 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC04" />
                        <path
                          d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.034466 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z"
                          fill="#EA4335"
                        />
                      </svg>
                      Google
                    </Button>
                    <Button 
                      variant={isDark ? "secondary" : "outline"} 
                      disabled={isLoading} 
                      className="social-button microsoft-button" 
                      onClick={handleMicrosoftSignIn}
                    >
                      <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                        <path d="M0 0H10V10H0V0Z" fill="#F25022" />
                        <path d="M11 0H21V10H11V0Z" fill="#7FBA00" />
                        <path d="M0 11H10V21H0V11Z" fill="#00A4EF" />
                        <path d="M11 11H21V21H11V11Z" fill="#FFB900" />
                      </svg>
                      Microsoft
                    </Button>
                  </div>
                  
                  {/* Enterprise SSO / IDP Login */}
                  <div className="mt-4">
                    <Button 
                      variant={isDark ? "secondary" : "outline"} 
                      disabled={isLoading} 
                      className="w-full text-(--login-text) bg-(--login-bg-secondary) border-(--login-border) border hover:bg-(--login-primary-hover)"
                      onClick={() => {
                        resetIDPDialog();
                        setShowIDPDialog(true);
                      }}
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      Login with Enterprise SSO
                    </Button>
                  </div>
                </>
              )}
            </div>

            <p className="terms-text">
              By clicking continue, you agree to our{' '}
              <a href="https://deiterate.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="terms-link">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="https://deiterate.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="terms-link">
                Privacy Policy
              </a>
              .
            </p>
            
            {/* Powered by footer with small logo */}
            <div className="powered-by">
              <img 
                src={isDark ? "/assets/img/large_logo_white.svg" : "/assets/img/large_logo.svg"} 
                alt="de.iterate" 
                style={{ width: '1rem', height: '1rem' }}
              />
              <span>Powered by de.iterate</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile view */}
      <div className="mobile-view">
        {/* Theme toggle - mobile */}
        <button 
          className="theme-toggle" 
          onClick={toggleTheme} 
          aria-label="Toggle theme"
          style={{ position: 'absolute', top: '1rem', right: '1rem' }}
        >
          {isDark ? <Sun /> : <Moon />}
        </button>
        
        {/* Duck mascot */}
        <div className="logo-container mobile-logo">
          <div className="logo-glow"></div>
          <img 
            src="/assets/ducks/compliant.svg" 
            alt="de.iterate Duck" 
            className="brand-logo" 
            style={{ width: '5rem', height: '5rem', borderRadius: '0' }}
          />
          <h1 className="brand-title" style={{ fontSize: '1.5rem', marginTop: '1rem' }}>de.iterate</h1>
        </div>
        
        <div className="mobile-form-container">
          {!showMFA ? (
            <div className="auth-header">
              <h1>Welcome back</h1>
              <p>Sign in with your credentials to continue</p>
            </div>
          ) : (
            <div className="auth-header">
              <h1>Two-factor Authentication</h1>
              <p>Enter the verification code sent to your phone</p>
            </div>
          )}

          {showPasswordReset ? (
            <div className="password-reset-form">
              <h2>Reset your password</h2>
              <p>Enter your email address and we'll send you a link to reset your password.</p>
              {showError && <div className="error-message">{errorMessage}</div>}
              <div className="form-group">
                <Input
                  id="reset-email-mobile"
                  placeholder="name@example.com"
                  type="email"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={isLoading || resetEmailSent}
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="mb-4"
                />
              </div>
              <div className="form-actions">
                <Button className="reset-button" disabled={isLoading || resetEmailSent} onClick={handlePasswordReset}>
                  {isLoading && (
                    <svg
                      className="spinner-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  )}
                  {resetEmailSent ? 'Email Sent' : 'Send Reset Link'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordReset(false);
                    setShowError(false);
                  }}
                  disabled={isLoading}>
                  Back to Login
                </Button>
              </div>
            </div>
          ) : showMFA ? (
            <div className="mfa-form">
              {showError && <div className="error-message">{errorMessage}</div>}
              <div className="form-group">
                <InputOTP maxLength={6} value={verificationCode} onChange={setVerificationCode} disabled={isLoading} className="mb-4">
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button className="verify-button" disabled={isLoading} onClick={handleVerifyCode}>
                {isLoading && (
                  <svg
                    className="spinner-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                )}
                Verify
              </Button>
            </div>
          ) : (
            <>
              <div className="mobile-form">
                {showError && <div className="error-message">{errorMessage}</div>}
                <Input
                  id="email-mobile"
                  placeholder="name@example.com"
                  type="email"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={isLoading}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mb-4"
                />
                <Input
                  id="password-mobile"
                  placeholder="Password"
                  type="password"
                  autoCapitalize="none"
                  autoComplete="current-password"
                  autoCorrect="off"
                  disabled={isLoading}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mb-4"
                />
                <div className="forgot-password">
                  <button
                    type="button"
                    className="forgot-password-link"
                    onClick={() => {
                      setShowPasswordReset(true);
                      setResetEmail(email);
                      setShowError(false);
                    }}>
                    Forgot password?
                  </button>
                </div>
                <Button className="mobile-sign-in-button" disabled={isLoading} onClick={onSubmit}>
                  {isLoading && (
                    <svg
                      className="spinner-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  )}
                  Sign In with Email
                </Button>
                {isSelfServiceEnabled ? (
                  <Button variant="outline" className="w-full mt-4" disabled={isLoading} onClick={() => navigate('/signup')}>
                    Sign Up
                  </Button>
                ) : null}
              </div>

              <div className="divider">
                <span>Or continue with</span>
              </div>

              <div className="mobile-social-buttons">
                <Button 
                  variant={isDark ? "secondary" : "outline"} 
                  className="mobile-social-button" 
                  disabled={isLoading} 
                  onClick={handleGoogleSignIn}
                >
                  <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3276 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z"
                      fill="#34A853"
                    />
                    <path d="M5.50253 14.3003C4.99987 12.8099 4.99987 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC04" />
                    <path
                      d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.034466 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z"
                      fill="#EA4335"
                    />
                  </svg>
                  Google
                </Button>
                <Button 
                  variant={isDark ? "secondary" : "outline"} 
                  className="mobile-social-button" 
                  disabled={isLoading} 
                  onClick={handleMicrosoftSignIn}
                >
                  <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                    <path d="M0 0H10V10H0V0Z" fill="#F25022" />
                    <path d="M11 0H21V10H11V0Z" fill="#7FBA00" />
                    <path d="M0 11H10V21H0V11Z" fill="#00A4EF" />
                    <path d="M11 11H21V21H11V11Z" fill="#FFB900" />
                  </svg>
                  Microsoft
                </Button>
              </div>
              
              {/* Enterprise SSO - Mobile */}
              <div className="mt-4 px-4">
                <Button 
                  variant={isDark ? "secondary" : "outline"} 
                  disabled={isLoading} 
                  className="w-full"
                  onClick={() => {
                    resetIDPDialog();
                    setShowIDPDialog(true);
                  }}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Enterprise SSO
                </Button>
              </div>
            </>
          )}

          <p className="mobile-terms-text">
            By clicking continue, you agree to our{' '}
            <a href="/terms" className="terms-link">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="terms-link">
              Privacy Policy
            </a>
            .
          </p>
          
          {/* Powered by footer with small logo */}
          <div className="powered-by">
            <img 
              src={isDark ? "/assets/img/large_logo_white.svg" : "/assets/img/large_logo.svg"} 
              alt="de.iterate" 
              style={{ width: '1rem', height: '1rem' }}
            />
            <span>Powered by de.iterate</span>
          </div>
        </div>
      </div>

      {/* IDP / SSO Discovery Dialog */}
      <Dialog open={showIDPDialog} onOpenChange={(open) => {
        setShowIDPDialog(open);
        if (!open) resetIDPDialog();
      }}>
        <DialogContent className="sm:max-w-[500px] max-w-4xl! p-0 gap-0 bg-transparent border-0 shadow-none">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border-none flex flex-col relative" style={{ backgroundColor: 'var(--login-input-bg)' }}>
            {/* Close button */}
            <button
              onClick={() => setShowIDPDialog(false)}
              disabled={idpLoading}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-muted/50 hover:bg-muted flex items-center justify-center transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Header */}
            <div className="p-8 pb-6 border-none text-center shrink-0" style={{ backgroundColor: 'var(--login-input-bg)' }}>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-primary/10 to-primary/30 mb-4">
                <Building2 className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-(--login-text)">Enterprise SSO Login</h2>
              <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                {idpStep === 'email' && 'Enter your work email to find your organization\'s SSO provider.'}
                {idpStep === 'discovered' && 'We found your organization\'s SSO provider.'}
                {idpStep === 'error' && 'Unable to find SSO configuration.'}
              </p>
            </div>

            {/* Form Content */}
            <div className="p-8 flex-1 min-h-0 overflow-y-auto space-y-6">
              {idpStep === 'email' && (
                <div className="rounded-xl border border-primary/10 backdrop-blur-sm p-5 transition-all hover:border-primary/30" style={{ backgroundColor: 'var(--login-card-bg)' }}>
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <label htmlFor="idp-email" className="block text-sm font-semibold text-(--login-text) mb-1">
                        Work Email <span className="text-destructive">*</span>
                      </label>
                      <p className="text-xs text-muted-foreground mb-3">
                        Enter your organization email address
                      </p>
                      <Input
                        id="idp-email"
                        type="email"
                        placeholder="you@company.com"
                        value={idpEmail}
                        onChange={(e) => setIdpEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleIDPDiscover()}
                        disabled={idpLoading}
                        className="border border-primary/10 h-11 focus:border-primary/50 text-(--login-text)`"
                        style={{ backgroundColor: 'var(--login-input-bg)', color: 'var(--login-text)' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {idpStep === 'discovered' && idpDiscoveryResult && (
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 backdrop-blur-sm p-6 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-emerald-500/20 to-emerald-500/40 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-emerald-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-lg font-semibold text-(--login-text) mb-1">
                        {idpDiscoveryResult.tenancyName || 'Your Organization'}
                      </p>
                      <p className="text-sm text-muted-foreground mb-3">
                        SSO Provider: {idpDiscoveryResult.ssoProvider?.displayName || idpDiscoveryResult.ssoProvider?.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Click continue to sign in with your organization's identity provider.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {idpStep === 'error' && (
                <div className="space-y-4">
                  <div className="rounded-xl border border-destructive/50 bg-destructive/10 backdrop-blur-sm p-6">
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-destructive/20 to-destructive/40 flex items-center justify-center">
                        <svg className="w-6 h-6 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-destructive mb-1">Unable to Find SSO Configuration</p>
                        <p className="text-xs text-muted-foreground">{idpError}</p>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setIdpStep('email');
                      setIdpError('');
                    }}
                  >
                    Try Again
                  </Button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-none shrink-0" style={{ backgroundColor: 'var(--login-input-bg)' }}>
              <div className="flex gap-3 justify-end">
                {idpStep === 'email' && (
                  <Button 
                    onClick={handleIDPDiscover} 
                    disabled={idpLoading || !idpEmail}
                    className="min-w-[140px]"
                  >
                    {idpLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Discovering...
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}

                {idpStep === 'discovered' && (
                  <Button 
                    onClick={handleSSOSignIn} 
                    disabled={idpLoading}
                    className="min-w-[160px]"
                  >
                    {idpLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        Sign in with SSO
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
